import React, {FC} from 'react'
import {useTranslation} from 'react-i18next'

import {AddPaymentMethodButtons} from 'src/products/MCU/AccountManagementV2/AddNewAccount/AddPaymentMethodButtons'
import {ButtonProps} from 'src/designSystem/components/atoms/Button/Button'
import {PaymentMethodList} from 'src/products/loans/MakePayment/LoanPayMethods/PaymentMethodList/PaymentMethodList'
import {PaymentMethodTypesEnum} from 'src/products/general/GeneralPaymentMethods/types'
import Box from 'src/designSystem/components/atoms/Box/Box'
import Page from 'src/designSystem/components/organisms/Page/Page'
import PFText from 'src/designSystem/components/atoms/PFText/PFText'
import {
  LoanPayMethodsAchPaymentMethod,
  LoanPayMethodsDebitCardPaymentMethod,
  LoanPayMethodsPaymentMethod,
} from 'src/products/loans/MakePayment/LoanPayMethods/LoanPayMethods.types'

/**
 * This template is named ViewPaymentMethodsTemplate rather than LoanPayMethodsTemplate because it is agnostic enough to be used for other products in the future.
 *
 * It is co-located with LoanPayMethods right now because this it the only place it is used.
 *
 * This template is used to display a list of payment methods.
 * It displays a list of available bank accounts and debit cards.
 * It allows the user to select a payment method or add a new payment method.
 */

export type ViewPaymentMethodTemplateProps = {
  achPaymentMethods: LoanPayMethodsAchPaymentMethod[]
  addPaymentMethodAccountType: PaymentMethodTypesEnum | null
  canAddMakePaymentInstrument: boolean
  debitPaymentMethods: LoanPayMethodsDebitCardPaymentMethod[]
  hideAggregatorButton?: boolean
  onContinue: ButtonProps['onPress']
  onSelectAddNewPaymentMethod: (accountType: PaymentMethodTypesEnum) => void
  onSelectExistingPaymentMethod: (paymentAccount: LoanPayMethodsPaymentMethod) => void
  selectedPaymentMethod: LoanPayMethodsPaymentMethod | null
}

export const ViewPaymentMethodsTemplate: FC<ViewPaymentMethodTemplateProps> = (props) => {
  const {
    achPaymentMethods,
    canAddMakePaymentInstrument,
    debitPaymentMethods,
    hideAggregatorButton,
    addPaymentMethodAccountType,
    onContinue,
    onSelectAddNewPaymentMethod: handleOnSelectAddNewPaymentMethod,
    onSelectExistingPaymentMethod: handleOnSelectExistingPaymentMethod,
    selectedPaymentMethod,
  } = props

  const {t} = useTranslation(['PaymentMethods', 'Common'])

  return (
    <Page
      buttonProps={{
        type: 'singleButton',
        primary: {
          text: t('Continue'),
          onPress: onContinue,
          disabled: !selectedPaymentMethod && addPaymentMethodAccountType === null,
          testID: 'ViewPaymentMethods-ContinueBtn',
        },
      }}
      variant={'generic'}
      title={t('PaymentMethods')}
      banner={(): null => null}
      smallTopGap
      noHeaderSpacer
    >
      <Box>
        <PFText variant={'h3'}>{t('BankAccounts')}</PFText>
        <Box marginTop={'small'}>
          <PaymentMethodList
            paymentMethods={achPaymentMethods}
            selectedPaymentMethod={selectedPaymentMethod}
            onPaymentAccountSelect={handleOnSelectExistingPaymentMethod}
          />
        </Box>
      </Box>

      <Box marginTop={'small'}>
        <PFText variant={'h3'}>{t('DebitCards')}</PFText>
        <Box marginTop={'small'}>
          <PaymentMethodList
            paymentMethods={debitPaymentMethods ?? []}
            selectedPaymentMethod={selectedPaymentMethod}
            onPaymentAccountSelect={handleOnSelectExistingPaymentMethod}
          />
        </Box>
      </Box>
      <Box marginTop={'medium'} marginBottom={'large'}>
        <Box gap={'little'}>
          <PFText variant={'h3'}>{t('AddNewPaymentMethod')}</PFText>
          <PFText variant={'p'}>{t('UseForOneTime')}</PFText>
        </Box>
        <Box marginTop={'large'} marginBottom={'medium'}>
          <AddPaymentMethodButtons
            selectedAccountType={addPaymentMethodAccountType}
            onSelectAccountType={handleOnSelectAddNewPaymentMethod}
            hideAggregatorButton={hideAggregatorButton}
            canAddMakePaymentInstrument={canAddMakePaymentInstrument}
          />
        </Box>
        <PFText variant={'p_sm'} color={'textDisabled'}>
          {t('UsingOnlineBank')}
        </PFText>
      </Box>
    </Page>
  )
}
